import type { ComponentProps } from 'react'
import type { League } from '../../types'
import OtherPlayerGameweekReveal from '../OtherPlayerGameweekReveal'
import ProfileLeagues from './ProfileLeagues'
import ProfileUtilityList from './ProfileUtilityList'

/**
 * The two profile variants, side by side.
 *
 * They live in one file, and as named components rather than a run of inline `isReadOnly &&`
 * checks inside `Profile`, because the difference between them is now a rule rather than a
 * detail: one carries every settings surface in the app and the other must carry none. That has
 * to be readable in one glance, not traced through five separate ternaries.
 */

/**
 * Your own profile: the utility list that reaches every other screen.
 *
 * The list is the *only* settings surface here.
 *
 * No leagues block: the Leagues tab already shows that exact list in full, one tap away. The
 * visited variant keeps its leagues because those are the *shared* ones, which exist nowhere else.
 *
 * No sign-out row: it lives on Settings, one tap further in, precisely so it cannot be mistapped
 * next to something else.
 */
export function OwnProfileSections({
  onOpenHowToPlay,
  onOpenSettings,
}: {
  onOpenHowToPlay: () => void
  onOpenSettings?: () => void
}) {
  return <ProfileUtilityList onOpenHowToPlay={onOpenHowToPlay} onOpenSettings={onOpenSettings} />
}

/**
 * Someone else's profile: their gameweek calls when the reveal window is open, the leagues you
 * share, and the rivalry action.
 *
 * Zero settings surface, by construction rather than by a hidden flag — there is no utility list
 * in this branch to hide. The crimson CTA is the one place on Profile it belongs.
 */
export function VisitedProfileSections({
  leagues,
  gameweekReveal,
  rivalStatus,
  onRivalAction,
  onConfirmEndRivalry,
}: {
  leagues: League[]
  gameweekReveal: ComponentProps<typeof OtherPlayerGameweekReveal> | null
  rivalStatus: 'none' | 'pending' | 'rival'
  onRivalAction?: () => void
  onConfirmEndRivalry: () => void
}) {
  return (
    <>
      {gameweekReveal && <OtherPlayerGameweekReveal {...gameweekReveal} />}

      <ProfileLeagues leagues={leagues} />

      <button
        type="button"
        className="profile__rival-btn"
        disabled={rivalStatus === 'pending'}
        onClick={() => (rivalStatus === 'rival' ? onConfirmEndRivalry() : onRivalAction?.())}
      >
        {rivalStatus === 'rival'
          ? 'Rival'
          : rivalStatus === 'pending'
            ? 'Pending'
            : 'Challenge'}
      </button>
    </>
  )
}
